/**
 * Admin Plan Management API service.
 *
 * Read-only client for the admin plan dashboard: aggregate summary, current
 * active plans, and the archive of ended subscriptions.
 *
 * @module adminPlanApi
 */

import { apiGet } from '@/lib/apiUtils';

/** Aggregate counts over current plans. */
export interface AdminPlanSummary {
  total_users: number;
  free: number;
  starter: number;
  exam_ready: number;
  full_prep: number;
  active_paid: number;
  expiring_soon: number;
  expired_pending: number;
  history_total: number;
}

/** A current plan row joined with its owning user. */
export interface AdminPlanItem {
  user_id: string;
  username: string;
  email?: string | null;
  plan_id: string;
  effective_plan_id: string;
  is_expired: boolean;
  chosen_level?: string | null;
  chosen_provider?: string | null;
  activated_at?: string | null;
  expires_at?: string | null;
}

export interface AdminActivePlansResponse {
  items: AdminPlanItem[];
  total: number;
}

/** An archived (ended) subscription joined with its owning user. */
export interface AdminPlanHistoryItem {
  id: string;
  user_id: string;
  username: string;
  plan_id: string;
  chosen_level?: string | null;
  chosen_provider?: string | null;
  activated_at?: string | null;
  expires_at?: string | null;
  ended_at: string;
  ended_reason: string;
}

export interface AdminPlanHistoryResponse {
  items: AdminPlanHistoryItem[];
  total: number;
}

/** Fetch aggregate plan counts for the dashboard summary cards. */
export const getPlanSummary = async (): Promise<AdminPlanSummary> => {
  const result = await apiGet<AdminPlanSummary>('/admin/plans/summary', {
    context: 'getPlanSummary',
    errorMessage: 'Failed to load plan summary',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to load plan summary');
  }
  return result;
};

/** Fetch a page of current plans (paid tiers only by default). */
export const getActivePlans = async (params: {
  paidOnly?: boolean;
  limit?: number;
  offset?: number;
}): Promise<AdminActivePlansResponse> => {
  const { paidOnly = true, limit = 50, offset = 0 } = params;
  const query = new URLSearchParams({
    paid_only: String(paidOnly),
    limit: String(limit),
    offset: String(offset),
  });
  const result = await apiGet<AdminActivePlansResponse>(
    `/admin/plans/active?${query.toString()}`,
    {
      context: 'getActivePlans',
      errorMessage: 'Failed to load active plans',
      throwOnError: true,
    },
  );
  if (!result) {
    throw new Error('Failed to load active plans');
  }
  return result;
};

/** Fetch a page of archived (ended) subscriptions. */
export const getPlanHistory = async (params: {
  limit?: number;
  offset?: number;
}): Promise<AdminPlanHistoryResponse> => {
  const { limit = 50, offset = 0 } = params;
  const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const result = await apiGet<AdminPlanHistoryResponse>(
    `/admin/plans/history?${query.toString()}`,
    {
      context: 'getPlanHistory',
      errorMessage: 'Failed to load plan history',
      throwOnError: true,
    },
  );
  if (!result) {
    throw new Error('Failed to load plan history');
  }
  return result;
};
